import * as ConfigValues from '../util/configValues';
import { WINDOW_SLEEP_MANAGER } from './WindowSleepManager';

export type LeadershipChangeCallback = (
  isLeader: boolean,
  leaderId: string | null,
  epoch: number
) => void;

interface BaseLeadershipMessage {
  kind: LeadershipMessageKind;
  tabId: string;
  ts: number;
}

interface LeaderAnnounceMessage extends BaseLeadershipMessage {
  kind: 'LEADER_ANNOUNCE';
  epoch: number;
}
interface HeartbeatMessage extends BaseLeadershipMessage {
  kind: 'HEARTBEAT';
  epoch: number;
}
interface LeadershipChallengeMessage extends BaseLeadershipMessage {
  kind: 'LEADERSHIP_CHALLENGE';
}
interface LeadershipClaimMessage extends BaseLeadershipMessage {
  kind: 'LEADERSHIP_CLAIM';
  epoch: number;
}

type LeadershipMessage =
  | LeaderAnnounceMessage
  | HeartbeatMessage
  | LeadershipChallengeMessage
  | LeadershipClaimMessage;

export type LeadershipMessageKind =
  | 'LEADER_ANNOUNCE'
  | 'HEARTBEAT'
  | 'LEADERSHIP_CHALLENGE'
  | 'LEADERSHIP_CLAIM';

// Timing constants
const HEARTBEAT_INTERVAL = 4000; // ms
const LEASE_EXPIRY = 12000; // ms without heartbeat -> lease expired
const ELECTION_TIMEOUT = 600; // ms collect claims
const CHANNEL_NAME = 'prio-tab-leadership';
const DEBUG = ConfigValues.REACT_APP_WS_DEBUG === 'true';
const HIDDEN_LEASE_FACTOR = 5; // extend lease while follower tab hidden
// Wake detection (system sleep/resume) - detect large timer drift
const WAKE_POLL_INTERVAL = 2000; // ms
const WAKE_DRIFT_THRESHOLD = 6000; // ms (gap > 6s between 2s ticks)

const debugInfo = (msg: string, ...args: any[]) => {
  if (DEBUG) {
    console.info(`[WS_DEBUG] ${msg}`, ...args);
  }
};
const nowMs = () => Date.now();
const rand = () => Math.floor(Math.random() * 1e6).toString(36);
const createTabId = () => `tab_${nowMs()}_${rand()}`;
export const tsOfTabId = (tabId: string): number => {
  const parts = tabId.split('_');
  if (parts.length < 3) return 0;
  const ts = parseInt(parts[1], 10);
  return isNaN(ts) ? 0 : ts;
};

enum ElectionState {
  Idle = 'Idle',
  Collecting = 'Collecting',
}

class TabLeaderServiceImpl {
  private channel: BroadcastChannel | null = null;
  private destroyed = false;
  private readonly tabId: string = createTabId();
  private currentLeaderId: string | null = null;
  private currentEpoch = 0;
  private lastLeaderBeat = 0;
  private isLeader = false;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private visibilityHandler: (() => void) | null = null;
  private leaseCheckTimer: ReturnType<typeof setInterval> | null = null;
  private wakeCheckTimer: ReturnType<typeof setInterval> | null = null;
  private lastWakeTick = Date.now();
  private electionTimer: ReturnType<typeof setTimeout> | null = null;
  private electionState: ElectionState = ElectionState.Idle;
  private pendingEpoch: number | null = null;
  private claims: Set<string> = new Set();
  private callbacks: Set<LeadershipChangeCallback> = new Set();

  constructor() {
    this.init();
  }

  // Public API
  isLeaderTab(): boolean {
    return this.isLeader;
  }
  leaderId(): string | null {
    return this.currentLeaderId;
  }
  epoch(): number {
    return this.currentEpoch;
  }
  onLeadershipChange(cb: LeadershipChangeCallback): () => void {
    this.callbacks.add(cb);
    // immediate notify
    cb(this.isLeader, this.currentLeaderId, this.currentEpoch);
    return () => {
      this.callbacks.delete(cb);
    };
  }
  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.stopHeartbeat();
    this.clearElection();
    if (this.leaseCheckTimer) {
      clearInterval(this.leaseCheckTimer);
      this.leaseCheckTimer = null;
    }
    if (this.wakeCheckTimer) {
      clearInterval(this.wakeCheckTimer);
      this.wakeCheckTimer = null;
    }
    if (this.channel) {
      try {
        this.channel.close();
      } catch (_) {
        /* noop */
      }
      this.channel = null;
    }
    window.removeEventListener('beforeunload', this.beforeUnloadHandler);
  }

  // Internal logic
  private init() {
    try {
      this.channel = new BroadcastChannel(CHANNEL_NAME);
      this.channel.addEventListener('message', (ev: MessageEvent) => {
        const msg: LeadershipMessage = ev.data;
        if (!msg || !msg.kind) return;
        this.handleMessage(msg);
      });
    } catch (e) {
      // TODO: localStorage fallback if BroadcastChannel unsupported
      debugInfo('[TabLeader] BroadcastChannel unavailable', e);
      return;
    }
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
    // Startup election
    debugInfo('[Leader] Startup election');
    this.startElection('startup');
    // Periodic lease check (followers)
    this.leaseCheckTimer = setInterval(() => this.checkLease(), 1000);
    // Wake (sleep) drift detector
    this.lastWakeTick = Date.now();
    this.wakeCheckTimer = setInterval(
      () => this.checkWake(),
      WAKE_POLL_INTERVAL
    );
    // Expose debug accessor
    if (DEBUG) {
      (window as any).__tabLeaderState = () => ({
        tabId: this.tabId,
        isLeader: this.isLeader,
        leaderId: this.currentLeaderId,
        epoch: this.currentEpoch,
        lastBeatAge: Date.now() - this.lastLeaderBeat,
        pendingEpoch: this.pendingEpoch,
        electionState: this.electionState,
      });
    }
  }

  private beforeUnloadHandler = () => {
    // Abdicate only on unload: simply stop heartbeats so others detect expiry.
    this.stopHeartbeat();
  };

  private emit(msg: LeadershipMessage) {
    if (!this.channel) return;
    try {
      this.channel.postMessage(msg);
    } catch (_) {
      /* ignore */
    }
  }

  private handleMessage(msg: LeadershipMessage) {
    if (this.destroyed) return;
    if (msg.tabId === this.tabId) return; // ignore own
    WINDOW_SLEEP_MANAGER.handleLastTimeAwake();
    switch (msg.kind) {
      case 'LEADER_ANNOUNCE':
        this.processLeaderAnnounce(msg);
        break;
      case 'HEARTBEAT':
        this.processHeartbeat(msg);
        break;
      case 'LEADERSHIP_CHALLENGE':
        // Respond if we are leader so challengers know
        if (this.isLeader) {
          this.announceLeader();
        }
        break;
      case 'LEADERSHIP_CLAIM':
        this.processClaim(msg);
        break;
    }
  }

  private processLeaderAnnounce(msg: LeaderAnnounceMessage) {
    // Accept higher epoch unconditionally.
    if (msg.epoch > this.currentEpoch) {
      debugInfo(
        '[Leader] Accept higher epoch announce',
        msg.epoch,
        'from',
        msg.tabId
      );
      this.acceptLeader(msg.tabId, msg.epoch, msg.ts);
      return;
    }
    if (msg.epoch === this.currentEpoch) {
      // Deterministic: older tab wins.
      if (!this.currentLeaderId) {
        debugInfo(
          '[Leader] Adopt leader (none known) ->',
          msg.tabId,
          'epoch',
          msg.epoch
        );
        this.acceptLeader(msg.tabId, msg.epoch, msg.ts);
        return;
      }
      if (this.currentLeaderId !== msg.tabId) {
        const existingTs = tsOfTabId(this.currentLeaderId);
        const incomingTs = tsOfTabId(msg.tabId);
        if (incomingTs < existingTs) {
          debugInfo('[Leader] Preempt older tab announce wins', msg.tabId);
          this.acceptLeader(msg.tabId, msg.epoch, msg.ts);
        }
      } else {
        this.lastLeaderBeat = msg.ts;
      }
    }
  }

  private processHeartbeat(msg: HeartbeatMessage) {
    if (msg.epoch < this.currentEpoch) return; // stale
    if (
      msg.epoch > this.currentEpoch ||
      (msg.epoch === this.currentEpoch &&
        this.currentLeaderId &&
        this.currentLeaderId !== msg.tabId)
    ) {
      // Maybe a new leader or preemption based on older tab id within same epoch.
      if (msg.epoch > this.currentEpoch) {
        debugInfo(
          '[Leader] Heartbeat higher epoch -> accept',
          msg.tabId,
          msg.epoch
        );
        this.acceptLeader(msg.tabId, msg.epoch, msg.ts);
        return;
      }
      if (msg.epoch === this.currentEpoch) {
        const existingTs = tsOfTabId(this.currentLeaderId!);
        const incomingTs = tsOfTabId(msg.tabId);
        if (incomingTs < existingTs) {
          debugInfo('[Leader] Heartbeat older tab preempts ->', msg.tabId);
          this.acceptLeader(msg.tabId, msg.epoch, msg.ts);
          return;
        }
      }
    } else if (this.currentLeaderId === msg.tabId) {
      this.lastLeaderBeat = msg.ts;
    }
  }

  private processClaim(msg: LeadershipClaimMessage) {
    // Collect claims only for pending epoch
    if (
      this.electionState === ElectionState.Collecting &&
      this.pendingEpoch === msg.epoch
    ) {
      this.claims.add(msg.tabId);
    }
  }

  private acceptLeader(tabId: string, epoch: number, beatTs: number) {
    const becameNew =
      this.currentLeaderId !== tabId || this.currentEpoch !== epoch;
    this.currentLeaderId = tabId;
    this.currentEpoch = epoch;
    this.lastLeaderBeat = beatTs;
    if (this.isLeader && tabId !== this.tabId) {
      // We thought we were leader but someone else (higher epoch / precedence) superseded.
      this.isLeader = false;
      this.stopHeartbeat();
    }
    if (becameNew && tabId !== this.tabId) {
      this.notifyChange();
    } else if (becameNew && tabId === this.tabId) {
      // Should not happen via acceptLeader
      this.notifyChange();
    }
    // Cancel any election in progress if leader is clear.
    if (this.electionState === ElectionState.Collecting) {
      this.clearElection();
    }
  }

  private startElection(reason: string) {
    if (this.electionState === ElectionState.Collecting || this.destroyed)
      return;
    this.electionState = ElectionState.Collecting;
    this.pendingEpoch = this.currentEpoch + 1; // always increment epoch for new election
    this.claims.clear();
    // Broadcast challenge & own claim
    this.emit({ kind: 'LEADERSHIP_CHALLENGE', tabId: this.tabId, ts: nowMs() });
    this.emit({
      kind: 'LEADERSHIP_CLAIM',
      tabId: this.tabId,
      ts: nowMs(),
      epoch: this.pendingEpoch,
    });
    debugInfo(
      '[Leader] Start election reason=',
      reason,
      'pendingEpoch=',
      this.pendingEpoch
    );
    this.claims.add(this.tabId);
    this.electionTimer = setTimeout(
      () => this.finalizeElection(),
      ELECTION_TIMEOUT
    );
    // If we already know an active leader with valid heartbeat, abort quickly.
    if (this.currentLeaderId && nowMs() - this.lastLeaderBeat < LEASE_EXPIRY) {
      this.clearElection();
    }
  }

  private finalizeElection() {
    if (this.electionState !== ElectionState.Collecting || this.destroyed)
      return;
    const epoch = this.pendingEpoch!;
    // If another leader has announced a higher epoch, abort.
    if (this.currentEpoch >= epoch) {
      this.clearElection();
      return;
    }
    // Determine winner = oldest tab id among claims
    const winner = Array.from(this.claims).sort(
      (a, b) => tsOfTabId(a) - tsOfTabId(b)
    )[0];
    debugInfo(
      '[Leader] Finalize election epoch',
      epoch,
      'winner',
      winner,
      'claims',
      Array.from(this.claims)
    );
    if (winner === this.tabId) {
      this.becomeLeader(epoch);
    } else {
      // Adopt winner as leader (no heartbeat yet; wait for announce)
      this.currentEpoch = epoch;
      this.currentLeaderId = winner;
      this.lastLeaderBeat = nowMs();
      this.notifyChange();
    }
    this.clearElection();
  }

  private clearElection() {
    this.electionState = ElectionState.Idle;
    this.pendingEpoch = null;
    this.claims.clear();
    if (this.electionTimer) {
      clearTimeout(this.electionTimer);
      this.electionTimer = null;
    }
  }

  private becomeLeader(epoch: number) {
    if (this.isLeader && this.currentEpoch === epoch) return;
    this.isLeader = true;
    this.currentLeaderId = this.tabId;
    this.currentEpoch = epoch;
    this.lastLeaderBeat = nowMs();
    this.notifyChange();
    this.announceLeader();
    // Send immediate heartbeat (do not wait full interval)
    this.emit({
      kind: 'HEARTBEAT',
      tabId: this.tabId,
      epoch: this.currentEpoch,
      ts: this.lastLeaderBeat,
    });
    this.startHeartbeat();
    this.attachVisibilityListener();
    debugInfo('[TabLeader] Became leader', { tabId: this.tabId, epoch });
  }

  private announceLeader() {
    this.emit({
      kind: 'LEADER_ANNOUNCE',
      tabId: this.tabId,
      epoch: this.currentEpoch,
      ts: nowMs(),
    });
  }

  private startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      if (!this.isLeader) return;
      const ts = nowMs();
      this.lastLeaderBeat = ts;
      this.emit({
        kind: 'HEARTBEAT',
        tabId: this.tabId,
        epoch: this.currentEpoch,
        ts,
      });
    }, HEARTBEAT_INTERVAL);
  }

  private stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    this.detachVisibilityListener();
  }

  private checkLease() {
    if (this.isLeader) return; // leader does not evaluate lease expiration relative to itself
    if (!this.currentLeaderId) {
      // No leader known -> ensure election started
      this.startElection('noLeaderKnown');
      return;
    }
    const elapsed = nowMs() - this.lastLeaderBeat;
    const allowed =
      document.visibilityState === 'hidden'
        ? LEASE_EXPIRY * HIDDEN_LEASE_FACTOR
        : LEASE_EXPIRY;
    if (elapsed > allowed) {
      debugInfo('[TabLeader] Lease expired, starting election', {
        leader: this.currentLeaderId,
        elapsed,
      });
      this.currentLeaderId = null;
      this.startElection('leaseExpired');
      return;
    }
    // Early stale detection: if we set a leader but never saw any heartbeat/announce beyond initial within > heartbeat interval *2
    if (elapsed > HEARTBEAT_INTERVAL * 2) {
      debugInfo('[Leader] Early stale leader detection - triggering election');
      this.currentLeaderId = null;
      this.startElection('earlyStale');
    }
  }

  private notifyChange() {
    for (const cb of Array.from(this.callbacks)) {
      try {
        cb(this.isLeader, this.currentLeaderId, this.currentEpoch);
      } catch (e) {
        // ignore faults from user callbacks
      }
    }
  }

  private checkWake() {
    const now = nowMs();
    const delta = now - this.lastWakeTick;
    this.lastWakeTick = now;
    if (delta < WAKE_POLL_INTERVAL + WAKE_DRIFT_THRESHOLD) return; // no significant drift
    debugInfo(
      '[Leader] Wake detected (drift ms)',
      delta,
      'isLeader=',
      this.isLeader
    );
    if (this.isLeader) {
      // Re-announce and heartbeat to refresh lease for followers
      this.lastLeaderBeat = now;
      this.announceLeader();
      this.emit({
        kind: 'HEARTBEAT',
        tabId: this.tabId,
        epoch: this.currentEpoch,
        ts: now,
      });
    } else {
      const elapsed = now - this.lastLeaderBeat;
      const allowed =
        document.visibilityState === 'hidden'
          ? LEASE_EXPIRY * HIDDEN_LEASE_FACTOR
          : LEASE_EXPIRY;
      if (!this.currentLeaderId || elapsed > allowed) {
        debugInfo(
          '[Leader] Wake -> lease expired or no leader, start election'
        );
        this.currentLeaderId = null;
        this.startElection('wakeLeaseExpired');
      } else {
        // Nudge current leader (challenge) to force a fresh announce quickly
        this.emit({ kind: 'LEADERSHIP_CHALLENGE', tabId: this.tabId, ts: now });
      }
    }
  }

  private attachVisibilityListener() {
    if (this.visibilityHandler) return;
    this.visibilityHandler = () => {
      if (!this.isLeader) return;
      const ts = nowMs();
      this.lastLeaderBeat = ts;
      this.emit({
        kind: 'HEARTBEAT',
        tabId: this.tabId,
        epoch: this.currentEpoch,
        ts,
      });
      debugInfo('[Leader] Visibility change heartbeat');
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  private detachVisibilityListener() {
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
  }
}

export class TabLeaderService extends TabLeaderServiceImpl {}

export const tabLeaderService = new TabLeaderService();
